import { ENDPOINTS } from "../../constant/routes";
import { request } from "../../services/requests";

export type OrderItemStatus =
  | "Pending Confirmation"
  | "Pending Delivery"
  | "In Delivery"
  | "Completed"
  | "Partially Refunded"
  | "Refunded";

export interface SalesItem {
  order_item_id: string;
  listing_id: string | null;
  name: string | null;
  genus: string | null;
  image: string | null;
  /** null when the viewer lacks sales_view_user_identity */
  seller: string | null;
  seller_key: string | null;
  status: OrderItemStatus | string;
  is_special_offer: boolean;
  quantity: number;
  unit_price: number;
  unit_delivery_fee: number;
  /** In the order's currency */
  subtotal: number;
  delivery: number;
  discount: number;
  net: number;
  /** In the reporting currency (SGD); null when no FX rate exists */
  net_reporting: number | null;
  discount_reporting: number | null;
  /** Inflos' fee, not counting a welcome voucher the seller paid for */
  platform_fee_reporting: number | null;
  /** Bring Your Buyers: standard | brought_buyer (0%) | brought_buyer_partial; null on older orders */
  fee_reason?: string | null;
  /** false for refunded items */
  counts_toward_revenue: boolean;
  tracking_number: string | null;
  delivery_provider: string | null;
  tracking_added_at: string | null;
  shipped_at: string | null;
}

export interface SalesOrder {
  order_id: string;
  created_at: string;
  buyer: string | null;
  buyer_key: string | null;
  buyer_country: string | null;
  is_first_order: boolean;
  currency: string;
  fx_rate: number | null;
  fx_missing: boolean;
  /** Created from the dashboard without payment ("Create order") */
  is_manual?: boolean;
  /** Admin who created it; null when identities are hidden or unknown */
  created_by?: string | null;
  items: SalesItem[];
  /** Welcome vouchers on this order, paid by the seller (already in item discounts) */
  vouchers?: SalesVoucher[];
}

export interface SalesVoucher {
  code: string | null;
  /** What the buyer got off, in `currency` */
  amount: number;
  currency: string;
  /** null when identities are hidden */
  seller: string | null;
  seller_key: string | null;
  /** The seller's share, in their earnings currency */
  seller_amount: number | null;
  seller_currency: string | null;
}

export interface SalesResponse {
  reporting_currency: string;
  include_identity: boolean;
  generated_at: string;
  orders: SalesOrder[];
}

/** Every paid order, all time. The page filters and aggregates locally. */
export const getSalesOrders = (token: string) =>
  request(token).get<SalesResponse>(`${ENDPOINTS.SALES}/orders`);

export interface MarkOrderCompleteResult {
  order_id: string;
  items_already_completed: number;
  items_completed: number;
  items_failed: { order_item_id: string; success: false; error: string }[];
  items_skipped: { order_item_id: string; order_status: string }[];
}

export const markOrderComplete = (orderId: string, token: string) =>
  request(token).put<MarkOrderCompleteResult>(
    `${ENDPOINTS.ORDERS}/${encodeURIComponent(orderId)}/complete`
  );

export interface RefundEarning {
  seller_id: string;
  seller: string;
  /** seller_earnings status before the refund */
  status: string;
  amount: number;
  currency: string;
  /** Already in a withdrawal request: can only be deducted from the next payout */
  withdrawn: boolean;
}

export interface RefundPreview {
  items: { order_item_id: string; status: string; quantity: number; price: number; delivery_fee: number; currency: string }[];
  earnings: RefundEarning[];
  already_refunded: boolean;
  /** What to refund in Stripe: the items' charge, net of vouchers and discount codes. */
  stripe_refund?: { amount: number; currency: string; discount: number } | null;
}

export interface RefundResult {
  order_id: string;
  items_refunded: number;
  earnings: { seller_id: string; amount: number; currency: string; action: "removed" | "deducted_from_next_payout" }[];
}

export const getRefundPreview = (orderId: string, token: string) =>
  request(token).get<RefundPreview>(`${ENDPOINTS.ORDERS}/${encodeURIComponent(orderId)}/refund-preview`);

export const refundOrder = (orderId: string, payload: { reason: string; deduct_withdrawn: boolean }, token: string) =>
  request(token).post<RefundResult>(`${ENDPOINTS.ORDERS}/${encodeURIComponent(orderId)}/refund`, payload);
